import { Injectable } from '@angular/core';
import { SelectedLocation } from '@next/core-lib/user';
import { ProductTypeEnum } from 'src/app/delivery/models/delivery.model';
import { DiscountSettings, FinalSetupSettings } from 'src/app/settings/models/setting.model';
import Utils from 'src/app/shared/utils';

import {
  ApplicabilityType,
  BalancePerDeliveryLine,
  Barn,
  Cycle,
  Discount,
  NweDiscountLabelEnum,
} from '../models/cycle.model';

@Injectable({
  providedIn: 'root',
})
export class DiscountService {
  private readonly countryNL = 'NL';
  private readonly roundPrecision = 50;

  constructor() {}

  public calculateAutomaticDiscountsForCycle(
    isFinalBalance: boolean,
    cycle: Cycle,
    finalSetupSettings: FinalSetupSettings,
    selectedLocation: SelectedLocation,
    discountSettings: DiscountSettings,
  ): Discount[] {
    if (!isFinalBalance || !finalSetupSettings?.masterData?.agrifirmMember) {
      return [];
    }

    const mixedFeedTotals = this.calculateMixedFeedTotals(cycle);
    const nextDiscountId = this.getNextDiscountId(cycle.discounts);

    const totalAmountOfChicks = this.calculateTotalAmountOfDayOldChicks(cycle);
    const barnChickMap = this.calculateBarnChickMap(cycle);

    if (selectedLocation.country !== this.countryNL) {
      const memberBenefitDiscounts = this.calculateMemberBenefit(
        nextDiscountId,
        mixedFeedTotals.totalPrice,
        barnChickMap,
        totalAmountOfChicks,
        discountSettings.memberBenefitAbroad,
      );

      return memberBenefitDiscounts;
    }

    const memberBenefitDiscounts = this.calculateMemberBenefit(
      nextDiscountId,
      mixedFeedTotals.totalPrice,
      barnChickMap,
      totalAmountOfChicks,
      discountSettings.memberBenefitNL,
    );

    const memberDiscounts = this.calculateMemberDiscounts(
      nextDiscountId + memberBenefitDiscounts.length,
      mixedFeedTotals.totalAmount,
      barnChickMap,
      totalAmountOfChicks,
      discountSettings.memberDiscountNL,
    );

    return [...memberBenefitDiscounts, ...memberDiscounts];
  }

  private calculateMemberBenefit(
    id: number,
    totalPrice: number,
    barnChickMap: Map<number, { barn: Barn; totalChicks: number }>,
    totalAmountOfChicks: number,
    discountPercentage: number,
  ): Discount[] {
    const discounts: Discount[] = [];
    let discountId = id;

    const totalDiscountAmount = totalPrice * (discountPercentage / 100);
    const roundedDiscountAmount = this.roundDown(totalDiscountAmount);

    barnChickMap.forEach((barnData) => {
      const discountAmount = roundedDiscountAmount * (barnData.totalChicks / totalAmountOfChicks);

      if (discountAmount === 0) {
        return;
      }

      discounts.push({
        id: discountId++,
        amount: discountAmount,
        applicabilityType: ApplicabilityType.FeedCost,
        label: NweDiscountLabelEnum.MemberBenefit,
        barn: barnData.barn,
        invoiceDate: Utils.parseDateTimeFromFormValue(new Date()),
        invoiceNumber: null,
        description: null,
        showInFinalSetup: false,
      });
    });

    return discounts;
  }

  private calculateMemberDiscounts(
    startId: number,
    totalAmount: number,
    barnChickMap: Map<number, { barn: Barn; totalChicks: number }>,
    totalAmountOfChicks: number,
    discountAmountPer100kg: number,
  ): Discount[] {
    const discounts: Discount[] = [];
    let discountId = startId;

    const totalDiscountAmount = (totalAmount / 100) * discountAmountPer100kg;
    const roundedDiscountAmount = this.roundDown(totalDiscountAmount);

    barnChickMap.forEach((barnData) => {
      const discountAmount = roundedDiscountAmount * (barnData.totalChicks / totalAmountOfChicks);

      if (discountAmount === 0) {
        return;
      }

      discounts.push({
        id: discountId++,
        amount: discountAmount,
        applicabilityType: ApplicabilityType.FeedCost,
        label: NweDiscountLabelEnum.MemberDiscount,
        barn: barnData.barn,
        invoiceDate: Utils.parseDateTimeFromFormValue(new Date()),
        invoiceNumber: null,
        description: null,
        showInFinalSetup: false,
      });
    });

    return discounts;
  }

  private calculateMixedFeedTotals(cycle: Cycle) {
    const mixedFeedTotals = cycle.balancesPerDeliveryLine
      .filter((b) => b.productType === ProductTypeEnum.MIXEDPRODUCT)
      .reduce(
        (totals, curr) => {
          const balanceAmount = this.sumBalanceAmounts(curr);

          totals.totalAmount += balanceAmount;
          totals.totalPrice += balanceAmount * curr.unitNettoPrice;

          return totals;
        },
        { totalPrice: 0, totalAmount: 0 },
      );

    return mixedFeedTotals;
  }

  private roundDown(value: number): number {
    return Math.floor(value / this.roundPrecision) * this.roundPrecision;
  }

  private sumBalanceAmounts(balance: BalancePerDeliveryLine) {
    return balance.barnBalances.reduce((total, balance) => total + balance.amount, 0);
  }

  private getNextDiscountId(discounts: Discount[]): number {
    return discounts && discounts.length > 0 ? Math.max(...discounts.map((d) => d.id)) + 1 : 1;
  }

  private calculateTotalAmountOfDayOldChicks(cycle: Cycle): number {
    return cycle.dayOldChicks.reduce((total, dayOldChick) => total + dayOldChick.amountOfChicks, 0);
  }

  private calculateBarnChickMap(cycle: Cycle): Map<number, { barn: Barn; totalChicks: number }> {
    return cycle.dayOldChicks.reduce((map, dayOldChick) => {
      const barnId = dayOldChick.barn.id;
      if (!map.has(barnId)) {
        map.set(barnId, { barn: dayOldChick.barn, totalChicks: 0 });
      }
      const barnData = map.get(barnId);

      if (barnData) {
        barnData.totalChicks += dayOldChick.amountOfChicks;
        map.set(barnId, barnData);
      }

      return map;
    }, new Map<number, { barn: Barn; totalChicks: number }>());
  }
}
